import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, MapPin, Navigation, CheckCircle, XCircle, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export const PostcodeChecker = () => {
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const handleLookup = async (e) => {
    e?.preventDefault();
    if (!postcode.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const resp = await api.get(`/postcodes/lookup`, { params: { postcode: postcode.trim() } });
      setResult(resp.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Postcode not found');
      } else {
        toast.error('Failed to look up postcode');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!result?.latitude || !mapRef.current) return;

    const initMap = async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');

      // Clean up previous map
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      const map = L.map(mapRef.current).setView(
        [(result.latitude + result.hq_latitude) / 2, (result.longitude + result.hq_longitude) / 2],
        9
      );
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      // Custom icons
      const targetIcon = L.divIcon({
        html: `<div style="background:#2563eb;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
        className: '',
      });
      const hqIcon = L.divIcon({
        html: `<div style="background:#f59e0b;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
        className: '',
      });

      L.marker([result.latitude, result.longitude], { icon: targetIcon })
        .addTo(map)
        .bindPopup(`<b>${result.postcode}</b><br/>${result.city}`)
        .openPopup();

      L.marker([result.hq_latitude, result.hq_longitude], { icon: hqIcon })
        .addTo(map)
        .bindPopup(`<b>Helios HQ</b><br/>${result.hq_postcode}`);

      // Draw line between points
      L.polyline(
        [[result.hq_latitude, result.hq_longitude], [result.latitude, result.longitude]],
        { color: '#6366f1', weight: 2, dashArray: '8, 8', opacity: 0.7 }
      ).addTo(map);

      // Fit bounds
      const bounds = L.latLngBounds(
        [result.hq_latitude, result.hq_longitude],
        [result.latitude, result.longitude]
      );
      map.fitBounds(bounds.pad(0.3));
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [result]);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="postcode-checker-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Postcode Checker</h1>
          <p className="text-slate-500 mt-1">Look up any UK postcode to check coverage and distance from HQ</p>
        </div>

        {/* Search */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <form onSubmit={handleLookup} className="flex gap-3">
              <div className="relative flex-1 max-w-md">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  placeholder="Enter postcode, e.g. MK11 3HA"
                  className="pl-10 text-lg tracking-wider"
                  data-testid="postcode-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !postcode.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                data-testid="postcode-search-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {loading ? 'Looking up...' : 'Search'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Details Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  {result.postcode}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Coverage Badge */}
                <div
                  data-testid="coverage-result"
                  className={`flex items-center gap-3 p-4 rounded-lg border ${
                    result.in_coverage
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  {result.in_coverage ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  <div>
                    <p className={`font-semibold ${result.in_coverage ? 'text-green-800' : 'text-red-800'}`}>
                      {result.in_coverage ? 'In Coverage Area' : 'Out of Coverage Area'}
                    </p>
                    <p className={`text-sm ${result.in_coverage ? 'text-green-600' : 'text-red-600'}`}>
                      {result.in_coverage
                        ? 'This postcode is within the Helios service area'
                        : 'This postcode is outside the standard service area'}
                    </p>
                  </div>
                </div>

                {/* Distance */}
                {result.distance_from_hq_miles != null && (
                  <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <Navigation className="w-6 h-6 text-indigo-600" />
                    <div>
                      <p className="font-semibold text-indigo-800">
                        {result.distance_from_hq_miles} miles from HQ
                      </p>
                      <p className="text-sm text-indigo-600">
                        Straight-line distance from {result.hq_postcode}
                      </p>
                    </div>
                  </div>
                )}

                {/* Address Details */}
                <div className="space-y-2 pt-2">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Location Details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['City / District', result.city],
                      ['County / Region', result.county],
                      ['Ward', result.ward],
                      ['Country', result.country],
                      ['Parish', result.parish],
                      ['Region', result.region],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label} className="p-2 bg-slate-50 rounded">
                        <p className="text-xs text-slate-500">{label}</p>
                        <p className="text-sm font-medium text-slate-800">{value}</p>
                      </div>
                    ))}
                  </div>
                  {result.latitude && (
                    <p className="text-xs text-slate-400 pt-1">
                      Coordinates: {result.latitude.toFixed(4)}, {result.longitude.toFixed(4)}
                    </p>
                  )}
                </div>

                {/* Add to New Contact */}
                <Button
                  onClick={() => navigate('/customers/new', {
                    state: {
                      postcode: result.postcode,
                      city: result.city || '',
                      state: result.county || '',
                    },
                  })}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-2"
                  data-testid="add-to-new-contact-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add to New Contact
                </Button>
              </CardContent>
            </Card>

            {/* Map Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-indigo-600" />
                  Map
                </CardTitle>
                <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-blue-600 inline-block" /> Searched postcode
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Helios HQ
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div
                  ref={mapRef}
                  data-testid="postcode-map"
                  className="w-full rounded-lg overflow-hidden border border-slate-200"
                  style={{ height: '400px' }}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PostcodeChecker;
